import axios from 'axios'

import { notificationToken } from 'src/composables/fcm/fcmToken.state'
import { apiUrl } from 'src/config'
import { router } from 'src/router'
import { idbService } from 'src/services/app/idb.service'
import { log } from 'src/services/app/log.service'
import { notifyService } from 'src/services/app/notify.service'
import { storageService } from 'src/services/app/storage.service'
import { API_ERRORS, FCM_ENDPOINTS, STORAGE_KEYS } from 'src/services/consts/api.const'
import { NOTIFICATION_MESSAGES } from 'src/services/consts/notification.const'
import { ROUTES } from 'src/services/consts/routes.const'
import { convertDateToUtcTS } from 'src/services/util.service'

let isHandlingAuthError = false
// Create axios instance
const httpClient = axios.create({
  baseURL: apiUrl,
  timeout: 1000 * 60 * 5, // 5 minutes
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
httpClient.interceptors.request.use(
  async (config) => {
    const isFormData = config.data instanceof FormData
    log.info('Making request', {
      data: isFormData ? '[FormData]' : config.data,
      url: config.url,
      method: config.method,
    })
    // Get accessToken and userId from vue-query
    const user = storageService.get(STORAGE_KEYS.LOGGED_IN_USER)
    // Set the accessToken and userId as BODY in the request
    try {
      if (user?.id) {
        await _validateUserSession(user)
        if (isFormData) {
          // For multipart uploads: append auth fields as form entries and
          // drop the JSON Content-Type so axios sets the multipart boundary.
          config.data.append('access_token', user.accessToken)
          config.data.append('user_id', user.id)
          delete config.headers['Content-Type']
        } else {
          config.data = {
            ...config.data,
            access_token: user.accessToken,
            user_id: user.id,
          }
        }
      }

      return config
    } catch (err) {
      log.error('Request error', err)
      if (err.status === 401) {
        await _handleAuthError(true)
        const error = new Error(API_ERRORS.AUTH_ERROR)
        error.isAuthError = true
        return Promise.reject(error)
      }
      return Promise.reject(err)
    }
  },
  (error) => {
    log.error('Request error', error)
    return Promise.reject(error)
  },
)

// Response interceptor
httpClient.interceptors.response.use(
  (response) => {
    // Check for error field in successful responses
    if (response.data && response.data.error) {
      log.error('API returned error in successful response', {
        url: response.config.url,
        error: response.data.error,
        data: response.data,
      })
      const error = new Error(response.data.error)
      error.response = response
      error.data = response.data
      return Promise.reject(error)
    }

    log.success('Request successful', {
      url: response.config.url,
      status: response.status,
      data: response.data,
    })

    return response.data
  },
  async (error) => {
    const { response, config } = error
    // Handle different error scenarios
    if (response) {
      log.error('Response error', {
        url: response.config.url,
        status: response.status,
        data: response.data,
      })

      // If is auth error, error will be handled in the request interceptor

      switch (response.status) {
        case 401:
          if (config?.url?.includes('auth')) {
            return Promise.reject(error)
          }

          await _handleAuthError(response.data.error === 'Unauthorized')
          // const authError = new Error(API_ERRORS.AUTH_ERROR)
          // authError.isAuthError = true
          // return Promise.reject(authError)
          break
        case 403:
          if (!config?.url?.includes('auth')) {
            notifyService.error(NOTIFICATION_MESSAGES.error.permissionDenied)
          }
          break
        case 404:
          // Handle not found
          break
        case 500:
          // Handle server error
          break
        default:
        // Handle other errors
      }
      log.error('Response error', { error })
    } else {
      if (error.isAuthError) {
        return
      }
      log.error('Network Error', { error })
    }

    return Promise.reject(error)
  },
)

// API methods
export const httpService = {
  get: (url, config = {}) => httpClient.get(url, config),
  post: (url, data = {}, config = {}) => httpClient.post(url, data, config),
  put: (url, data = {}, config = {}) => httpClient.put(url, data, config),
  delete: (url, config = {}) => httpClient.delete(url, config),
  patch: (url, data = {}, config = {}) => httpClient.patch(url, data, config),
}

export default httpService

// Authentication helpers
async function _validateUserSession(user) {
  const { accessTokenExpires } = user
  const hasAccessTokenExpired = _validateToken(accessTokenExpires)
  if (hasAccessTokenExpired) throw { status: 401, message: 'Access token has expired' }
}

function _validateToken(tokenExpires) {
  if (!tokenExpires) return false

  const tokenTime = convertDateToUtcTS(new Date(tokenExpires))
  const now = convertDateToUtcTS(new Date())
  return now > tokenTime
}

async function _handleAuthError(isAuthError = false) {
  // Prevent recursive calls
  if (isHandlingAuthError) return
  isHandlingAuthError = true

  try {
    // Get user data before clearing storage
    const user = storageService.get(STORAGE_KEYS.LOGGED_IN_USER)

    // Raw axios bypasses request interceptor session validation so unsubscribe
    // still reaches the server when the access token has expired (inlined — avoid firebase.service cycle)
    if (user?.id && notificationToken.value) {
      await axios.post(
        `${apiUrl}${FCM_ENDPOINTS.UNSUBSCRIBE_FROM_ALL_TOPICS}`,
        {
          fcm_token: notificationToken.value,
          access_token: user.accessToken,
          user_id: user.id,
        },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 1000 * 60 * 5,
        },
      )
      storageService.remove(STORAGE_KEYS.FCM_TOKEN_CACHE)
    }
  } catch (error) {
    log.error('Error unsubscribing during auth error:', error)
  }

  // Clear storage and navigate
  localStorage.clear()
  storageService.clearAllSession() // Clear session storage
  idbService.removeData(STORAGE_KEYS.COMPANIES)
  router.push({ name: ROUTES.LOGIN })

  if (isAuthError) {
    notifyService.error(NOTIFICATION_MESSAGES.sessionExpired, 'auth-error')
  }

  isHandlingAuthError = false
}
